import { expectDefined } from '@utils';
import { z } from 'zod';
import { DEFAULT_LOCALE_ID, type LocaleId, LocaleId as LocaleIdEnum } from './LocaleId';

export interface IStringObject {
  variants: Record<string, string>;
  description?: string;
}

export type IStringMap = Map<LocaleId, string>;

export type IStringTuples = [LocaleId, string][];

export type IStringVariants =
  | IString
  | IStringMap
  | IStringTuples
  | IStringObject
  | string;

/**
 * Represents an immutable string with multiple locale variants and an optional description.
 *
 * @remarks
 * This class is designed to store localized string variants mapped by locale identifiers.
 * Once created, the variants and description cannot be modified.
 *
 * @example
 * ```typescript
 * const variants = new Map<LocaleId, string>([
 *   ['en', 'Hello'],
 *   ['fr', 'Bonjour']
 * ]);
 * const greeting = iString(variants, 'A greeting message');
 * console.log(greeting.get('en')); // 'Hello'
 * ```
 *
 * @immutable
 */
export class IString {
  private _description?: string;
  private _variants: Map<LocaleId, string>;
  private _fallbackLocale?: LocaleId;

  constructor(variants?: IStringVariants, description?: string) {
    if (variants instanceof IString) {
      this._description = variants._description;
      this._variants = variants._variants;
      this._fallbackLocale = variants._fallbackLocale;
      return;
    }

    if (
      typeof variants === 'object' &&
      !Array.isArray(variants) &&
      'variants' in variants &&
      typeof variants.variants === 'object' &&
      !Array.isArray(variants.variants)
    ) {
      this._variants = new Map(Object.entries(variants.variants)) as Map<
        LocaleId,
        string
      >;
      this._description = variants.description;
      return;
    }

    if (typeof variants === 'string') {
      this._variants = new Map([[DEFAULT_LOCALE_ID, variants]]);
      this._description = description;
      return;
    }

    if (variants === undefined) {
      variants = IString.EMPTY._variants;
    }

    if (variants instanceof Map) {
      if (variants.size === 0) {
        throw new Error('IString must have at least one locale variant');
      }
      this._variants = variants;
      this._description = description;
    } else if (Array.isArray(variants)) {
      if (variants.length === 0) {
        throw new Error('IString must have at least one locale variant');
      }
      const map = new Map<LocaleId, string>();
      for (const entry of variants) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!Array.isArray(entry) || entry.length !== 2) {
          throw new Error('Each variant tuple must be a [LocaleId, string] pair');
        }
        const [locale, value] = entry;
        if (typeof locale !== 'string') {
          throw new Error('LocaleId must be a string');
        }
        if (typeof value !== 'string') {
          throw new Error('Variant value must be a string');
        }
        map.set(locale, value);
      }
      this._variants = map;
      this._description = description;
    } else {
      throw new Error(`Invalid variants input for IString: ${JSON.stringify(variants)}`);
    }

    // If there is only one variant and it's not the default locale, then use it
    // as the fallback.
    if (this._variants.size === 1 && !this._variants.has(DEFAULT_LOCALE_ID)) {
      this._fallbackLocale = expectDefined(this._variants.keys().next().value);
    }
  }

  static EMPTY = new IString('');

  get isEmpty(): boolean {
    return this._variants.size === 1 && this._variants.get(DEFAULT_LOCALE_ID) === '';
  }

  equals(other: IString): boolean {
    if (this === other) return true;
    if (!(other instanceof IString)) return false;
    if (this._description !== other._description) return false;
    if (this._variants.size !== other._variants.size) return false;
    for (const [locale, value] of this._variants) {
      if (other._variants.get(locale) !== value) {
        return false;
      }
    }
    return true;
  }

  get(locale?: LocaleId): string {
    let result = '';
    if (locale !== undefined) {
      result = this._variants.get(locale) ?? '';
    }
    if (result === '' && this._fallbackLocale !== undefined) {
      result = this._variants.get(this._fallbackLocale) ?? '';
    }
    if (result === '') {
      result = this._variants.get(DEFAULT_LOCALE_ID) ?? '';
    }
    return result;
  }

  get description(): string | undefined {
    return this._description;
  }

  toJSON(): unknown {
    // If there's no description and only a single default-locale variant, serialize as a string literal
    if (
      this._description === undefined &&
      this._variants.size === 1 &&
      this._variants.has(DEFAULT_LOCALE_ID)
    ) {
      const val = this._variants.get(DEFAULT_LOCALE_ID);
      if (typeof val === 'string') {
        return val;
      }
    }
    return {
      description: this._description,
      variants: Object.fromEntries(this._variants)
    };
  }

  static fromJSON(json: unknown): IString {
    return IStringSchema.parse(json);
  }

  entries(): [LocaleId, string][] {
    return Array.from(this._variants.entries());
  }

  toString(): string {
    // Single default-locale case
    if (this._variants.size === 1 && this._variants.has(DEFAULT_LOCALE_ID)) {
      const only = this._variants.get(DEFAULT_LOCALE_ID);
      return JSON.stringify(only);
    }
    // Order: default locale first (if present), then remaining locales alpha by locale id
    const defEntry = this._variants.get(DEFAULT_LOCALE_ID);
    const rest = Array.from(this._variants.entries())
      .filter(([loc]) => loc !== DEFAULT_LOCALE_ID)
      .sort((a, b) => a[0].localeCompare(b[0]));
    const ordered = defEntry ? [[DEFAULT_LOCALE_ID, defEntry], ...rest] : rest;
    const parts = ordered.map(([loc, str]) => {
      const lit = JSON.stringify(str);
      // When multiple variants, always include ~locale (including default) for disambiguation
      if (ordered.length > 1) return `${lit}~${loc}`;
      return lit; // single variant case already handled above, but keep fallback
    });
    return parts.join(' ');
  }
}

// Schema for serializing/deserializing IString JSON representation
export const IStringSchema = z.union([
  z.instanceof(IString),
  z
    .object({
      description: z.string().optional(),
      variants: z.record(z.nativeEnum(LocaleIdEnum), z.string())
    })
    .transform(({ description, variants }) => {
      const pairs = Object.entries(variants) as [LocaleId, string][];
      return new IString(new Map(pairs), description);
    }),
  z.string().transform(str => new IString(str))
]);

export function iString(variants: IStringVariants, description?: string): IString {
  return new IString(variants, description);
}
